// Colapsa el pattern `useEffect + fetch + cancelled + setState(loading|error|data)`
// duplicado en ~8 componentes. La función pasada se re-ejecuta cuando cambia
// alguna dependencia; el AbortSignal permite cortar la request en vuelo si el
// componente se desmonta o las deps cambian antes de que resolviera.
//
// Uso:
//   const { data, loading, error } = useAsync(
//     (signal) => api.getClassroom(id, { signal }),
//     [id],
//   );
//
// Convención: la función SIEMPRE recibe el signal como primer argumento. Si el
// caller no lo propaga a `fetch`, no hay cancelación real — solo `data` no se
// setea post-desmontaje (guardia via `cancelled`).
import { useEffect, useState } from 'react';

export type AsyncState<T> =
  | { loading: true; data: null; error: null }
  | { loading: false; data: T; error: null }
  | { loading: false; data: null; error: string };

export function useAsync<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({
    loading: true,
    data: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    const abortCtrl = new AbortController();
    setState({ loading: true, data: null, error: null });

    fn(abortCtrl.signal)
      .then((data) => {
        if (cancelled) return;
        setState({ loading: false, data, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ loading: false, data: null, error: message });
      });

    return () => {
      cancelled = true;
      abortCtrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
